import axios from "axios";

/** Default socket inactivity timeout on pooled agents (must exceed axios request timeout). */
const DEFAULT_SOCKET_ACTIVE_TIMEOUT_MS = 60000;

/**
 * Creates HTTP/HTTPS agents with keep-alive pooling for server-side requests.
 * Uses Node's built-in agents (Node 18+ supports scheduling, timeout, and pooling).
 *
 * @param {number} socketActiveTimeoutMs - Max inactivity on a socket before destroy.
 * @returns {{ httpAgent: import("http").Agent, httpsAgent: import("https").Agent }}
 */
function createServerAgents(socketActiveTimeoutMs) {
  const http = require("http");
  const https = require("https");

  const poolConfig = {
    keepAlive: true,
    maxSockets: 400,
    maxFreeSockets: 100,
    scheduling: "lifo",
    timeout: socketActiveTimeoutMs,
  };

  return {
    httpAgent: new http.Agent(poolConfig),
    httpsAgent: new https.Agent(poolConfig),
  };
}

/**
 * Represents an HTTP client for making API requests with advanced keepAlive support.
 * Uses Node.js native connection pooling on the server.
 */
export class HttpClient {
  /**
   * Create a new instance of HttpClient.
   * @param {string} baseUrl - The base URL for the HTTP requests.
   */
  constructor(baseUrl) {
    /**
     * HTTP and HTTPS Agents with keepAlive configuration.
     * Only used in Node.js environment (server-side).
     */
    let httpAgent = null;
    let httpsAgent = null;

    // Axios request timeout (default 5s). Separate from agent socket lifetime so slow-but-successful
    // responses do not destroy pooled sockets and force expensive createConnection on the next call.
    const requestTimeout = parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || "5000", 10);
    const socketActiveTimeout = parseInt(String(DEFAULT_SOCKET_ACTIVE_TIMEOUT_MS), 10);

    // Only create agents server-side (Node.js environment)
    if (typeof window === "undefined") {
      const agents = createServerAgents(socketActiveTimeout);
      httpAgent = agents.httpAgent;
      httpsAgent = agents.httpsAgent;
    }

    /**
     * The Axios instance for making HTTP requests.
     * @type {Object}
     *
     * Note: Axios automatically selects the agent based on URL protocol:
     * - URLs starting with "https://" → uses httpsAgent
     * - URLs starting with "http://" → uses httpAgent
     * This is built-in Axios behavior, no manual selection needed.
     */
    this.instance = axios.create({
      baseURL: baseUrl,
      withCredentials: true,
      timeout: requestTimeout, // Request timeout from NEXT_PUBLIC_API_TIMEOUT (default 5s)
      httpAgent, // HTTP agent for keepAlive (server-side only) - used for http:// URLs
      httpsAgent, // HTTPS agent for keepAlive (server-side only) - used for https:// URLs
    });

    // Store timeout for use in request methods
    this.requestTimeout = requestTimeout;

    /**
     * The token used for authentication.
     * @type {string}
     */
    this.token = "";

    // Initialize request and response interceptors
    this._initializeRequestInterceptor();
    this._initializeResponseInterceptor();
  }

  /**
   * Initialize the response interceptor for the Axios instance.
   * @private
   */
  _initializeResponseInterceptor = () => {
    this.instance.interceptors.response.use(this._handleResponse, this._handleError);
  };

  /**
   * Initialize the request interceptor for the Axios instance.
   * @private
   */
  _initializeRequestInterceptor = () => {
    this.instance.interceptors.request.use(this._handleRequest, this._handleError);
  };

  /**
   * Handle the request by adding the token to the request headers.
   * @param {Object} config - Axios request configuration.
   * @returns {Object} - Modified request configuration.
   * @private
   */
  _handleRequest = (config) => {
    if (config.url?.includes("homepage")) {
      // config.headers["Content-Type"] = "application/json";
    }
    return config;
  };

  /**
   * Handle the response by extracting the data.
   * When `config.returnHeaders` is true, merges response headers into the returned payload.
   * @param {Object} response - Axios response object.
   * @returns {Object} - Response data (optionally with headers).
   * @private
   */
  _handleResponse = (response) => {
    const data = response?.data;
    if (response?.config?.returnHeaders) {
      const payload = data && typeof data === "object" ? data : {};
      return { ...payload, headers: response.headers };
    }
    return data;
  };

  /**
   * Handle errors by rejecting the Promise.
   * @param {Object} error - Axios error object.
   * @returns {Promise} - Rejected Promise.
   * @private
   */
  _handleError = (error) => {
    console.error("Error: ", error?.response);
    return { status: error?.response?.status, data: error?.response?.data };
  };
}
